import { gql } from 'graphql-request';

import { CurrencyISO } from '@/constants/currencies';
import { ISO2 } from '@/constants/countries';

export type Company = {
  id: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  exchangeCountryISO: ISO2;
  primaryIndustry: {
    name: string;
    friendlyName: string;
    industryAverages: {
      name: string;
      value: number;
    }[];
  };
  secondaryIndustry: {
    name: string;
    friendlyName: string;
    industryAverages: {
      name: string;
      value: number;
    }[];
  };
  info: {
    url: string | null;
  };
  score: {
    value: number;
    future: number;
    past: number;
    health: number;
    dividend: number;
    sentence: string;
  } | null;
  analysisDividend: {
    dividendYield: number | null;
  };
  data: {
    currencyInfo: {
      trading_item_currency_iso: CurrencyISO;
      reporting_unit: number;
    };
    marketCap: {
      shares_outstanding: number;
    };
  };
  analysisFuture: {
    revenueGrowthAnnual: number | null;
    forwardPe1Y: number | null;
    forwardPriceToSales1Y: number | null;
    netIncomeGrowthAnnual: number | null;
  };
  analysisHealth: {
    netIncome: number | null;
  };
  analysisValue: {
    pe: number | null;
    lastSharePrice: number | null;
    npvPerShare: number | null;
    intrinsicValue: {
      cost_of_equity: number | null;
    };
    pb: number | null;
    priceToSales: number | null;
    preferredRelativeMultiple: 'pe' | 'ps' | 'pb' | string | null;
    intrinsicDiscount: number | null;
    return7d: number | null;
    return30d: number | null;
    return90d: number | null;
    return1yAbs: number | null;
    marketCapUSD: number | null;
    priceTarget: number | null;
    priceTargetAnalystCount: number | null;
  };
  analysisPast: {
    netIncome: number | null;
    netIncomeMargin: number | null;
    revenue: number | null;
    netIncomeLtmHistory: {
      [key: string]: number;
    };
    revenueLtmHistory: {
      [key: string]: number;
    };
  };
  analysisMisc: {
    analystCount: number;
    sharesOutstandingSingleGrowth1Y: number | null;
  };
  canonicalURL: string;
};

export const COMPANY_FRAGMENT = gql`
  fragment CompanyFields on Company {
    id
    name
    tickerSymbol
    uniqueSymbol
    data {
      marketCap
      currencyInfo
    }
    analysisFuture {
      revenueGrowthAnnual
      forwardPe1Y
    }
    exchangeCountryISO
    info {
      url
    }
    primaryIndustry {
      name
      friendlyName
      industryAverages {
        name
        value
      }
    }
    secondaryIndustry {
      name
      friendlyName
      industryAverages {
        name
        value
      }
    }
    analysisDividend {
      dividendYield
    }
    score {
      value
      future
      past
      health
      dividend
      sentence
    }
    analysisValue {
      intrinsicValue
      pe
      pb
      priceToSales
      preferredRelativeMultiple
      lastSharePrice
      npvPerShare
      marketCapUSD
      intrinsicDiscount
      return7d
      return30d
      return90d
      return1yAbs
      priceTarget
      priceTargetAnalystCount
    }
    analysisPast {
      netIncome
      netIncomeMargin
      revenue
      netIncomeLtmHistory
      revenueLtmHistory
    }
    analysisMisc {
      analystCount
      sharesOutstandingSingleGrowth1Y
    }
    canonicalURL
  }
`;
